
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { ElateList, Elate } from '../model/elate.model';
import { AIProvidersIncidentsResponse } from '../model/ai.model';
import { ServiceValidity } from '../model/service-validity.model';
import { parisToday, toParisDay } from '../utils/paris-day.util';
import { MaterialService } from './material.service';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class ElateService extends KiwixiService {
    private apiElate = environment.server + 'elates/';
    private apiFlashLevelLevelAttestationDownload = environment.server + 'flashlevel_level_attestation/generatepdf/';

    constructor(private _http: HttpClient,
        private _router: Router,
        kiwixiGlobals: KiwixiGlobals,
        private materialService: MaterialService,
    ) {
        super(_http, _router, kiwixiGlobals, materialService);
    }

    getElates(params: HttpParams): Observable<ElateList> {
        return this._http.get<ElateList>(this.apiElate, { params })
    }

    getElateWithUrl(_url: string): Observable<ElateList> {
        return this._http.get<ElateList>(_url);
    }

    getElateFromId(id: number): Observable<Elate> {
        return this._http.get<Elate>(this.apiElate + id + '/');
    }

    /**
     * Incidents fournisseurs IA durant le test FlashLevel.
     * GET /api/v1/ai-providers-incidents/?elate_id=<id>
     */
    getAiProvidersIncidents(elateId: number): Observable<AIProvidersIncidentsResponse> {
        const header = this.createHttpClientAuthorizationHeader();
        return this._http.get<AIProvidersIncidentsResponse>(
            environment.server + 'ai-providers-incidents/?elate_id=' + elateId,
            { headers: header }
        );
    }

    getElateFromToken(token: string): Observable<Elate> {
        return this._http.get<Elate>(environment.base_server + '/api/external/v1/elates_token/' + token);
    }

    /**
     * True si la réponse API indique un test annulé
     * (FlashLevel ou LILATE : 403 + status cancelled_*).
     */
    static isCancelledTestError(err: any): boolean {
        const body = err?.error;
        const status = body?.status;
        return (
            err?.status === 403
            && typeof status === 'string'
            && status.indexOf('cancelled_') === 0
        );
    }

    /** @deprecated utiliser isCancelledTestError */
    static isCancelledFlashLevelError(err: any): boolean {
        return ElateService.isCancelledTestError(err);
    }

    /**
     * True si l'API refuse un test expiré (400 ou 403), par exemple au démarrage
     * d'un FlashLevel dont la date butoir est passée. Le refus porte la date
     * butoir (`expired_date`) ; le statut peut être encore « en attente » si le
     * cron d'expiration n'est pas passé.
     */
    static isExpiredTestError(err: any): boolean {
        const body = err?.error;
        return (
            (err?.status === 403 || err?.status === 400)
            && (body?.status === 'expired' || !!body?.expired_date)
        );
    }

    /**
     * True si le FlashLevel est expiré : statut `expired`, ou FlashLevel en attente
     * dont la date butoir est passée (jour de Paris) avant le passage du cron.
     * Le FlashLevel est relu à chaque ouverture du lien : après une réactivation,
     * le même lien redevient utilisable.
     */
    static isExpiredElate(elate: Elate | null | undefined): boolean {
        const status = elate?.elate_rec?.status ?? elate?.status;
        if (status === 'expired') {
            return true;
        }
        if (status !== 'pending') {
            return false;
        }
        const deadline = toParisDay(elate?.elate_rec?.expired_date ?? elate?.expired_date);
        return !!deadline && deadline < parisToday();
    }

    /** Navigation vers la page « FlashLevel expiré ». */
    static expiredPageCommands(): { commands: any[]; extras: { queryParams: Record<string, string> } } {
        return { commands: ['/error'], extras: { queryParams: { reason: 'expired' } } };
    }

    /**
     * Navigation vers /error avec le message métier « annulé » ou « expiré » si applicable.
     */
    static errorPageCommands(err?: any): { commands: any[]; extras?: { queryParams?: Record<string, string> } } {
        if (ElateService.isCancelledTestError(err)) {
            return { commands: ['/error'], extras: { queryParams: { reason: 'cancelled' } } };
        }
        if (ElateService.isExpiredTestError(err)) {
            return ElateService.expiredPageCommands();
        }
        return { commands: ['/error'] };
    }

    patchElateFromToken(token: string, data: any): Observable<Elate> {
        return this._http.patch<Elate>(environment.base_server + '/api/external/v1/elates_token/' + token, data);
    }

    /**
     * Génère le prompt contextuel IA (mode candidate) via le token FlashLevel.
     * POST /api/external/v1/flashlevel/<token>/generate-prompt-context/
     */
    generatePromptContextFromToken(
        token: string,
        data: {
            ai_job?: string | null;
            ai_business?: string | null;
            ai_company_name?: string | null;
            ai_website?: string | null;
        },
    ): Observable<{
        ai_prompt_context: string;
        ai_job: string | null;
        ai_business: string | null;
        ai_company_name: string | null;
        ai_website: string | null;
    }> {
        return this._http.post<{
            ai_prompt_context: string;
            ai_job: string | null;
            ai_business: string | null;
            ai_company_name: string | null;
            ai_website: string | null;
        }>(
            environment.base_server + '/api/external/v1/flashlevel/' + token + '/generate-prompt-context/',
            data,
        );
    }

    patchElate(id: number, data: Partial<Elate>): Observable<Elate> {
        return this._http.patch<Elate>(this.apiElate + id + '/', data);
    }

    /**
     * Fenêtre de validité du FlashLevel et droits d'action (staff).
     * GET /elates/<id>/validity/
     */
    getValidity(elateId: number): Observable<ServiceValidity> {
        return this._http.get<ServiceValidity>(`${this.apiElate}${elateId}/validity/`);
    }

    /**
     * Prolonge la date butoir d'un FlashLevel seul : action unique, date calculée par Engage.
     * POST /elates/<id>/extend-validity/
     */
    extendValidity(elateId: number): Observable<ServiceValidity> {
        return this._http.post<ServiceValidity>(`${this.apiElate}${elateId}/extend-validity/`, {});
    }

    /**
     * Réactive un FlashLevel seul expiré : action unique, date calculée par Engage.
     * POST /elates/<id>/reactivate/
     */
    reactivate(elateId: number): Observable<ServiceValidity> {
        return this._http.post<ServiceValidity>(`${this.apiElate}${elateId}/reactivate/`, {});
    }

    getStatus() {
        return [
            { label: 'En attente', code: 'pending', valid: true },
            { label: 'En cours', code: 'ongoing', valid: true },
            { label: 'Réalisé', code: 'done', valid: true },
            { label: 'Expiré', code: 'expired', valid: true },
            { label: 'Abandonné', code: 'abandoned', valid: true },
            { label: 'Annulé par l\'admin', code: 'cancelled_by_admin', valid: true },
        ];
    }

    getElateNotifications(elateId: number, pageSize: number = 25): Observable<any> {
        const params = new HttpParams().set('page_size', pageSize.toString());
        return this._http.get<any>(this.apiElate + elateId + '/notifications/', { params });
    }

    getElateNotificationsWithUrl(url: string): Observable<any> {
        return this._http.get<any>(url);
    }

    getCancelStatus() {
        return [
            { code: 'duplicate', label: 'Doublon', valid: true },
            { code: 'refuse', label: 'Refuse de passer le test', valid: true },
            { code: 'other', label: 'Autre', valid: true },
        ];
    }

    getElateNotificationById(elateId: number, notificationId: number): Observable<any> {
        return this._http.get<any>(`${this.apiElate}${elateId}/notifications/${notificationId}/`);
    }

    getElateLogs(elateId: number): Observable<any> {
        return this._http.get<any>(`${this.apiElate}${elateId}/logs/`).pipe(
            catchError(err => of({ ok: false, error: err }))
        );
    }

    // --- Export XLSX des logs ---
    exportElateLogs(elateId: number): Observable<Blob> {
        return this._http.get(`${this.apiElate}${elateId}/logs-export/`, { responseType: 'blob' });
    }

    // --- Télécharger certificat FlashLevel ---
    downloadFlashLevelLevelAttestationPdf(elate: Elate) {
        this._http.get(this.apiFlashLevelLevelAttestationDownload + elate.token, { responseType: 'arraybuffer' })
            .subscribe((file: ArrayBuffer) => {
                const _name = '_' + elate.student.user.first_name + '_' + elate.student.user.last_name;
                const filename = 'attestation_niveau_flashlevel_' + _name;
                this.downloadFile(filename, file, 'application/pdf');
            });
    }

    /**
     * Mapping entre les codes de langue (language.xx) et les valeurs à envoyer dans le formulaire Elate
     * Utilise le code de langue (ex: 'language.fr') et non le label (ex: 'Français - Langue Étrangère')
     * pour garantir une correspondance fiable et éviter les problèmes avec les labels multiples.
     * 
     * @param languageCode - Code de la langue (ex: 'language.fr', 'language.en')
     * @returns La valeur à envoyer dans le formulaire ('Français', 'Anglais', etc.)
     */
    getLanguageFormValue(languageCode: string): string {
        const languageMapping: { [key: string]: string } = {
            'language.en': 'Anglais',
            'language.es': 'Espagnol',
            'language.de': 'Allemand',
            'language.it': 'Italien',
            'language.he': 'Hébreu',
            'language.lsf': 'Langue des signes',
            'language.zh': 'Chinois',
            'language.ja': 'Japonais',
            'language.hi': 'Hindi',
            'language.ru': 'Russe',
            'language.pt': 'Portugais',
            'language.pl': 'Polonais',
            'language.no': 'Norvégien',
            'language.fr': 'Français',  // Français - Langue Étrangère
            'language.gr': 'Grec',
            'language.id': 'Indonésien',
            'language.lsb': 'Langue des signes pour bébé',
            'language.ar': 'Arabe',
            'language.sv': 'Suédois',
            'language.sr': 'Serbe',
            'language.hr': 'Croate',
            'language.br': 'Portugais Brésilien',
            'language.co': 'Coréen',
            'language.ga': 'Irlandais',
            'language.nl': 'Hollandais',
            'language.dk': 'Danois',
            'language.yue': 'Cantonais'
        };

        return languageMapping[languageCode] || languageCode;
    }

    /**
     * Récupère le code de langue depuis une valeur de formulaire
     * (opération inverse de getLanguageFormValue)
     * 
     * @param formValue - Valeur du formulaire (ex: 'Français', 'Anglais')
     * @returns Le code de la langue (ex: 'language.fr', 'language.en') ou null si non trouvé
     */
    getLanguageCodeFromFormValue(formValue: string): string | null {
        const reverseMapping: { [key: string]: string } = {
            'Anglais': 'language.en',
            'Espagnol': 'language.es',
            'Allemand': 'language.de',
            'Italien': 'language.it',
            'Hébreu': 'language.he',
            'Langue des signes': 'language.lsf',
            'Chinois': 'language.zh',
            'Japonais': 'language.ja',
            'Hindi': 'language.hi',
            'Russe': 'language.ru',
            'Portugais': 'language.pt',
            'Polonais': 'language.pl',
            'Norvégien': 'language.no',
            'Français': 'language.fr',  // Mappé vers Français - Langue Étrangère
            'Grec': 'language.gr',
            'Indonésien': 'language.id',
            'Langue des signes pour bébé': 'language.lsb',
            'Arabe': 'language.ar',
            'Suédois': 'language.sv',
            'Serbe': 'language.sr',
            'Croate': 'language.hr',
            'Portugais Brésilien': 'language.br',
            'Coréen': 'language.co',
            'Irlandais': 'language.ga',
            'Hollandais': 'language.nl',
            'Danois': 'language.dk',
            'Cantonais': 'language.yue'
        };

        return reverseMapping[formValue] || null;
    }

    /**
     * Retourne la configuration TTS complète selon la langue de l'interface
     * Utilise les nouveaux modèles et voix (OpenAI gpt-4o-mini-tts et ElevenLabs)
     * 
     * @param lang - Code de langue de l'interface (ex: 'fr', 'en', 'es', 'it')
     * @returns Configuration TTS avec provider, model, voice et speed
     */
    getTtsConfigByLanguage(lang: string): { provider: string; model: string; voice: string; speed: number } {
        const ttsMapping: { [key: string]: { provider: string; model: string; voice: string; speed: number } } = {
            'fr': { provider: 'elevenlabs', model: 'eleven_flash_v2_5', voice: 'WeAAwKYcS06VmXw086yZ', speed: 1.0 },
            'it': { provider: 'elevenlabs', model: 'eleven_flash_v2_5', voice: 'pWHqWjkaSNybDOvgMt58', speed: 1.0 },
            'es': { provider: 'elevenlabs', model: 'eleven_flash_v2_5', voice: 'SKjgN71N3MeGl4r2JbRt', speed: 1.0 },
            'en': { provider: 'openai', model: 'gpt-4o-mini-tts', voice: 'nova', speed: 1.0 }
        };

        // Fallback sur 'en' si langue non trouvée
        return ttsMapping[lang] || ttsMapping['en'];
    }
}
